import React, { useState } from 'react'
import { generateLocalSummary } from '../../utils/youtubeSummary'
import { youtubeSummaryApi } from '../../config/api'

const YoutubeSummaryPage = () => {
  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [summary, setSummary] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [videoInfo, setVideoInfo] = useState(null)
  const [success, setSuccess] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [analysisData, setAnalysisData] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')

  const extractVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
    const match = url.match(regExp)
    return (match && match[2].length === 11) ? match[2] : null
  }

  const isValidYoutubeUrl = (url) => {
    const patterns = [
      /^https?:\/\/(www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
      /^https?:\/\/youtu\.be\/([a-zA-Z0-9_-]{11})/,
      /^https?:\/\/(www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/
    ]
    return patterns.some(pattern => pattern.test(url))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSummary('')
    setVideoInfo(null)
    setSuccess(false)
    setTranscript('')
    setAnalysisData(null)

    if (!youtubeUrl.trim()) {
      setError('유튜브 URL을 입력해주세요.')
      return
    }

    if (!isValidYoutubeUrl(youtubeUrl)) {
      setError('올바른 유튜브 URL을 입력해주세요.')
      return
    }

    setIsLoading(true)

    try {
      const videoId = extractVideoId(youtubeUrl)
      
      // 공통 API 함수 사용
      const data = await youtubeSummaryApi.generateSummary(videoId, youtubeUrl)

      console.log('📥 Frontend 받은 데이터:', data)
      console.log('📥 Transcript 길이:', data.videoInfo?.transcript?.length || 0)
      console.log('📥 AnalysisData:', data.analysisData)

      if (data.success && data.summary) {
        setSummary(data.summary)
        setVideoInfo(data.videoInfo)
        setTranscript(data.videoInfo?.transcript || '')
        setAnalysisData(data.analysisData || null)
        setSuccess(true)
        
        console.log('✅ Frontend 상태 업데이트 완료')
        console.log('✅ Transcript 상태:', data.videoInfo?.transcript?.length || 0)
        console.log('✅ AnalysisData 상태:', data.analysisData ? '있음' : '없음')
      } else {
        throw new Error(data.error || '요약 데이터를 받지 못했습니다.')
      }
      
      // 로컬 개발용 fallback (API 실패 시)
      /*
      const summaryText = await generateLocalSummary(videoId, youtubeUrl)
      setSummary(summaryText)
      */
    } catch (err) {
      console.error('YouTube summary error:', err)
      setError(err.message || '요약 생성 중 오류가 발생했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClear = () => {
    setYoutubeUrl('')
    setSummary('')
    setError('')
    setVideoInfo(null)
    setSuccess(false)
    setTranscript('')
    setAnalysisData(null)
    setSaveMessage('')
  }

  const handleSaveToDatabase = async () => {
    if (!videoInfo || !transcript || !summary) {
      setSaveMessage('❌ 저장할 데이터가 없습니다.')
      return
    }

    setIsSaving(true)
    setSaveMessage('')

    try {
      const saveData = {
        title: videoInfo.title,
        description: videoInfo.description,
        channelTitle: videoInfo.channelTitle,
        videoUrl: youtubeUrl,
        videoId: extractVideoId(youtubeUrl),
        videoScript: transcript,
        videoSummary: summary,
        thumbnail: videoInfo.thumbnail,
        duration: videoInfo.duration,
        viewCount: videoInfo.viewCount,
        likeCount: videoInfo.likeCount,
        commentCount: videoInfo.commentCount,
        publishedAt: videoInfo.publishedAt
      }

      console.log('💾 저장할 데이터:', saveData)

      const response = await youtubeSummaryApi.saveYoutubeSummary(saveData)

      if (response.success) {
        setSaveMessage(`✅ 성공적으로 저장되었습니다! (ID: ${response.postId})`)
      } else {
        setSaveMessage(`❌ 저장 실패: ${response.error}`)
      }
    } catch (err) {
      console.error('저장 오류:', err)
      setSaveMessage(`❌ 저장 중 오류가 발생했습니다: ${err.message}`)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="youtube-summary-page">
      <div className="page-header">
        <h2>📺 유튜브 영상 요약</h2>
        <p>유튜브 링크를 입력하면 영상 내용을 요약해드립니다.</p>
      </div>

      <div className="summary-form-section card">
        <form onSubmit={handleSubmit} className="summary-form">
          <div className="form-group">
            <label htmlFor="youtube-url">유튜브 URL</label>
            <div className="input-group">
              <input
                type="url"
                id="youtube-url"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="form-input"
                disabled={isLoading}
              />
              <button 
                type="button" 
                onClick={handleClear}
                className="clear-btn"
                disabled={isLoading}
              >
                🗑️
              </button>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-btn"
              disabled={isLoading || !youtubeUrl.trim()}
            >
              {isLoading ? (
                <>
                  <span className="loading-spinner">🔄</span>
                  요약 생성 중...
                </>
              ) : (
                '📝 요약 생성'
              )}
            </button>
          </div>
        </form>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

            {success && (
              <div className="success-message">
                <span className="success-icon">✅</span>
                <span>요약이 성공적으로 생성되었습니다!</span>
                <div className="save-section">
                  <button 
                    onClick={handleSaveToDatabase}
                    disabled={isSaving}
                    className="save-btn"
                  >
                    {isSaving ? (
                      <>
                        <span className="loading-spinner">🔄</span>
                        저장 중...
                      </>
                    ) : (
                      '💾 데이터베이스에 저장'
                    )}
                  </button>
                  {saveMessage && (
                    <div className={`save-message ${saveMessage.includes('✅') ? 'success' : 'error'}`}>
                      {saveMessage}
                    </div>
                  )}
                </div>
              </div>
            )}
      </div>

      {youtubeUrl && !error && (
        <div className="video-preview-section card">
          <h3>영상 미리보기</h3>
          <div className="video-container">
            <iframe
              width="560"
              height="315"
              src={`https://www.youtube.com/embed/${extractVideoId(youtubeUrl)}`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}

      {transcript && (
        <div className="transcript-section card">
          <h3>📝 영상 자막 (전사본)</h3>
          <div className="transcript-content">
            <div className="transcript-info">
              <span className="transcript-length">
                📏 자막 길이: {transcript.length.toLocaleString()}자
              </span>
              <span className="transcript-status">
                {transcript.length > 100 ? '✅ 자막 있음' : '⚠️ 자막 부족'}
              </span>
            </div>
            <div className="transcript-text">
              <pre>{transcript}</pre>
            </div>
          </div>
          <div className="transcript-actions">
            <button 
              className="copy-btn"
              onClick={() => navigator.clipboard.writeText(transcript)}
            >
              📋 자막 복사
            </button>
          </div>
        </div>
      )}

      {analysisData && (
        <div className="analysis-section card">
          <h3>🤖 AI 분석 결과</h3>
          <div className="analysis-content">
            {analysisData.summary && (
              <div className="analysis-item">
                <h4>📄 핵심 요약</h4>
                <p>{analysisData.summary}</p>
              </div>
            )}
            
            {analysisData.keyPoints && analysisData.keyPoints.length > 0 && (
              <div className="analysis-item">
                <h4>📋 주요 포인트</h4>
                <ul>
                  {analysisData.keyPoints.map((point, index) => (
                    <li key={index}>{point}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {analysisData.keywords && analysisData.keywords.length > 0 && (
              <div className="analysis-item">
                <h4>🔑 핵심 키워드</h4>
                <div className="keywords">
                  {analysisData.keywords.map((keyword, index) => (
                    <span key={index} className="keyword-tag">#{keyword}</span>
                  ))}
                </div>
              </div>
            )}
            
            {analysisData.purpose && (
              <div className="analysis-item">
                <h4>🎯 영상 목적</h4>
                <p>{analysisData.purpose}</p>
              </div>
            )}
            
            {analysisData.value && (
              <div className="analysis-item">
                <h4>💎 영상 가치</h4>
                <p>{analysisData.value}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {summary && (
        <div className="summary-result-section card">
          <h3>📋 전체 요약 결과</h3>
          <div className="summary-content">
            <pre className="summary-text">{summary}</pre>
          </div>
          <div className="summary-actions">
            <button 
              className="copy-btn"
              onClick={() => navigator.clipboard.writeText(summary)}
            >
              📋 복사
            </button>
            <button 
              className="download-btn"
              onClick={() => {
                const blob = new Blob([summary], { type: 'text/plain' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = 'youtube-summary.txt'
                a.click()
                URL.revokeObjectURL(url)
              }}
            >
              💾 다운로드
            </button>
          </div>
        </div>
      )}

      <div className="info-section card">
        <h3>ℹ️ 사용 방법</h3>
        <ul>
          <li>유튜브 영상 URL을 입력하세요</li>
          <li>지원되는 URL 형식: youtube.com/watch?v=, youtu.be/, youtube.com/embed/</li>
          <li>요약 생성 버튼을 클릭하면 영상 내용을 요약합니다</li>
          <li>요약 결과를 복사하거나 텍스트 파일로 다운로드할 수 있습니다</li>
        </ul>
      </div>
    </div>
  )
}

export default YoutubeSummaryPage
